import * as React from 'react';
import { GoogleMap } from '@react-google-maps/api';
import {
  DriverPin,
  MapLabelPin,
  MapSurface,
  type DriverPinTone,
} from '@relay-towing/ui';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { Pinned } from './Pinned';

export interface MapCall {
  id: string;
  dispatchNumber: string;
  status: string;
  pickupLocationLat: number | null;
  pickupLocationLong: number | null;
}

export interface MapTruck {
  lat: number;
  lng: number;
  label: string;
  initials: string;
  /** What the driver is doing, in the pin's colour. Available when left out. */
  tone?: DriverPinTone;
}

/** Two letters for a truck's pin: "MW" for Marcus Webb. */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const FILL = { width: '100%', height: '100%' };
// San Diego, where most fleets are, until the pins say otherwise.
const FALLBACK_CENTRE = { lat: 32.7157, lng: -117.1611 };
const MAP_OPTIONS = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: 'greedy',
};

/** The call has a pickup to pin: without one there is nothing to map. */
export const hasPoint = (call: MapCall) =>
  typeof call.pickupLocationLat === 'number' &&
  typeof call.pickupLocationLong === 'number' &&
  !(call.pickupLocationLat === 0 && call.pickupLocationLong === 0);

/**
 * The app's Google map with the library's pins: each call at its pickup and
 * each truck where it last reported: a driver's own, on the calls list. The
 * calls and trucks are the lists the
 * screen already loaded, which the server scoped; the map adds none of its
 * own, so a driver sees their calls and the office sees the company's.
 */
export function CallsMap({
  calls,
  trucks = [],
  onOpenCall,
  overlay,
  framed,
  caption,
  className,
}: {
  calls: MapCall[];
  trucks?: MapTruck[];
  onOpenCall?: (id: string) => void;
  overlay?: React.ReactNode;
  /** A card in the screen's gutter, as the calls list has it. */
  framed?: boolean;
  /** What the map is, in a glass label at the bottom: "Truck map". */
  caption?: React.ReactNode;
  className?: string;
}) {
  const { isLoaded } = useGoogleMaps();
  const pinned = calls.filter(hasPoint);

  const fit = React.useCallback(
    (map: google.maps.Map) => {
      const points = [
        ...pinned.map((call) => ({
          lat: call.pickupLocationLat as number,
          lng: call.pickupLocationLong as number,
        })),
        ...trucks.map((truck) => ({ lat: truck.lat, lng: truck.lng })),
      ];
      if (points.length === 0) return;
      if (points.length === 1) {
        map.setCenter(points[0]);
        map.setZoom(13);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, 48);
    },
    [pinned, trucks]
  );

  return (
    <MapSurface
      className={className}
      overlay={overlay}
      framed={framed}
      caption={caption}
      loadingLabel={isLoaded ? undefined : 'Loading the map'}
    >
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={FILL}
          center={FALLBACK_CENTRE}
          zoom={10}
          options={MAP_OPTIONS}
          onLoad={fit}
        >
          {trucks.map((truck) => (
            <Pinned
              key={`${truck.label}-${truck.lat}-${truck.lng}`}
              lat={truck.lat}
              lng={truck.lng}
              anchor="dot"
            >
              <DriverPin
                initials={truck.initials}
                tone={truck.tone ?? 'available'}
                label={truck.label}
              />
            </Pinned>
          ))}
          {pinned.map((call) => (
            <Pinned
              key={call.id}
              lat={call.pickupLocationLat as number}
              lng={call.pickupLocationLong as number}
            >
              <MapLabelPin
                tone={call.status === 'NEW' ? 'waiting' : 'pickup'}
                onClick={onOpenCall ? () => onOpenCall(call.id) : undefined}
              >
                #{call.dispatchNumber}
              </MapLabelPin>
            </Pinned>
          ))}
        </GoogleMap>
      ) : null}
    </MapSurface>
  );
}
